import { Inject, Injectable } from "@angular/core";
import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { SURVIVES_ROUTE_CHANGE } from "./cancelhttpbetweenroutes.interceptor";

import { ISaleSave, ISaleDialogState, ISale } from "../model/sale.model";
import { ICreateDialogState, IDocumentDialogstate, IMdoItem, IPreference, IPreferencesDialogstate, ITasksDialogstate, IVisibleFor } from "../model/dialog-state.model";
import { ICompanySummary, IContactSummary, IArchiveListItem, Followup } from "../model/summary.model";
import {
  IContactOrPerson, IContactPrefillRequest, IContactPrefillResponse, ICreateCompanyOrContact, IFindSenderByMail
} from "../model/contact-or-person.model";
import { ICompanyCard, IPersonCard } from "../model/contact-card.model";
import { IArchive } from "../model/archive.model";
import { Entity } from "./shared.service";
import { OutlookService } from "./outlook/outlook.service";
import { IProject } from "../model/project.model";
import { ICapabilitiesPrivileged, ITicketMailBoxes, ITicketMessageMailBoxes } from "../model/shared.model";
import { AppointmentEntity, VisibleFor } from "../model/appointmententity.model";
import { CultureDataFormatter } from "../utilities/CultureDataFormatter";
import { IAppointmentExtension } from "../model/appointmentExtension.model";
import { ICustomData } from "../model/customData.model";
import { catchError, defer, firstValueFrom, from, map, Observable, of, switchMap, throwError } from "rxjs";
import { MonitoringService } from "./monitoring.service";
import { AuthService } from "./auth.service";
import { UserInfoService } from "./userinfo.service";
import { SharedMailboxService } from "./shared-mailbox.service";
import { aiSummaryErrorCodes, AiSummaryError, AiSummaryErrorCode, AiThreadSummaryResponse } from "../utilities/forms/save-form/ai-summary/ai-summary-api.service";

// Untagged errors (timeout, offline, unknown shape) become a retryable generationFailed. A 404
// has no body - it's [RequiresFeature] disabling the tenant/user - so status is checked first.
function toAiSummaryError(error: unknown): AiSummaryError {
  if ((error as HttpErrorResponse)?.status === 404) {
    return new AiSummaryError("featureDisabled");
  }

  const body = (error as HttpErrorResponse)?.error;
  const code = typeof body === "string" ? body : body?.code;
  return new AiSummaryError(
    aiSummaryErrorCodes.includes(code) ? code as AiSummaryErrorCode : "generationFailed");
}

// Must match TicketController.EmailTooLargeProblemType.
export function isEmailTooLargeProblem(error: unknown): boolean {
  return (error as HttpErrorResponse)?.error?.type === "email-too-large";
}

export interface IPreUploadByMessageBatchResponse {
  WithAttachmentsPreUploadId: string | null;
  WithoutAttachmentsPreUploadId: string | null;
}

export type PreuploadDecisionMode = "both" | "withoutOnly" | "blocked";

export interface IPreUploadByMessageDecisionResponse {
  Mode: PreuploadDecisionMode;
}

interface IPreUploadByMessageDecisionRawResponse {
  Mode?: PreuploadDecisionMode;
  mode?: PreuploadDecisionMode;
}

interface IPreUploadByMessageBatchRawResponse {
  WithAttachmentsPreUploadId?: string | null;
  WithoutAttachmentsPreUploadId?: string | null;
  withAttachmentsPreUploadId?: string | null;
  withoutAttachmentsPreUploadId?: string | null;
}

interface INaaAuthOptions {
  operation: string;
  allowInteractive?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class SoApiService {
  private preferences: IPreferencesDialogstate;
  private capabilities: ICapabilitiesPrivileged[];
  private namedPreferences: NamedPreference[];
  private readonly documentDialogstateCache = new Map<string, IDocumentDialogstate>();
  private readonly documentDialogstateInFlight = new Map<string, Promise<IDocumentDialogstate>>();

  constructor(
    private readonly httpClient: HttpClient,
    private readonly outlookService: OutlookService,
    private readonly monitoringService: MonitoringService,
    private readonly authService: AuthService,
    private readonly userInfo: UserInfoService,
    @Inject("BASE_URL") private readonly baseUrl: string,
    private readonly sharedMailboxService: SharedMailboxService
  ) { }

  /* generic httpClient wrappers, giving us nice promises, instead of cumbersome observables */

  /**
   * Fire-and-forget POST using fetch keepalive — guaranteed to complete even during page unload.
   * Must be used instead of HttpClient for window unload cleanup calls.
   */
  private beaconPost(urlSuffix: string, body: unknown): void {
    const token = this.userInfo.clientAppBearer;
    if (!token) {
      return;
    }

    void fetch(this.baseUrl + urlSuffix, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      keepalive: true,
    });
  }

  private post<TRequest, TResponse>(urlSuffix: string, request: TRequest, httpHeaders: HttpHeaders = null): Promise<TResponse> {
    return firstValueFrom(this.observablePost<TRequest, TResponse>(urlSuffix, request, httpHeaders));
  }

  private observablePost<TRequest, TResponse>(urlSuffix: string, request: TRequest, httpHeaders: HttpHeaders = null): Observable<TResponse> {
    const url = this.baseUrl + urlSuffix;
    return this.httpClient.post<TResponse>(url, request, { headers: httpHeaders });
  }
  private delete<TResponse>(urlSuffix: string): Promise<TResponse> {
    const url = this.baseUrl + urlSuffix;
    return firstValueFrom(this.httpClient.delete<TResponse>(url));
  }

  private get<TResponse>(urlSuffix: string): Promise<TResponse> {
    return firstValueFrom(this.observableGet<TResponse>(urlSuffix));
  }

  private observableGet<TResponse>(urlSuffix: string): Observable<TResponse> {
    const url = this.baseUrl + urlSuffix;
    return this.httpClient.get<TResponse>(url);
  }
  /* sale service replacement */

  getSalesDialogState(
    SenderEmailAddress: string,
    SenderName: string,
    UniqueId: string,
    UniqueIds: string[]
  ): Observable<ISaleDialogState> {
    return this.observablePost("api/sale/dialogstate", {
      SenderEmailAddress,
      SenderName,
      UniqueId,
      UniqueIds,
    }, this.httpHeaders);
  }

  async getContextSerialNumber(): Promise<string> {
    return await this.get("api/licence/serial");
  }
  async createSale(sale: ISaleSave): Promise<number> {
    return await this.post("api/sale", sale);
  }

  /* people service replacement */

  private async getActivities(restrictions: string): Promise<Followup[]> {
    const archiveList: IArchiveListItem[] = await this.get(`api/appointment?query=${encodeURIComponent(restrictions)}`);

    const tasks: Followup[] = new Array();

    if (!archiveList) { //if canceled, this will be null
      return null;
    }

    for (const item of archiveList) {
      const task: Followup = {
        AppointmentId: CultureDataFormatter.parseInteger(item.ColumnData.appointmentId?.DisplayValue),
        Completed: "" + CultureDataFormatter.parseInteger(item.ColumnData.completed.DisplayValue),
        Date: CultureDataFormatter.parseDate(item.ColumnData.date.DisplayValue),
        Description: item.ColumnData.text.DisplayValue,
        DocumentId: CultureDataFormatter.parseInteger(item.ColumnData.documentId?.DisplayValue),
        Registered: CultureDataFormatter.parseDate(item.ColumnData.registeredDate.DisplayValue)
      };
      tasks.push(task);
    }

    return tasks;
  }

  async getCompanySummary(companyId: number): Promise<ICompanySummary> {
    return await this.get("api/contact/" + companyId + "/summary");
  }

  async getPersonSummary(personId: number): Promise<IContactSummary> {
    return await this.get("api/person/" + personId + "/summary");
  }

  async getCompanyActivities(companyId: number): Promise<Followup[]> {
    return this.getActivities(`contactId=${companyId} and (date beforeToday or date today)`);
  }

  async getPersonActivities(personId: number): Promise<Followup[]> {
    return this.getActivities(`personId=${personId} and (date beforeToday or date today)`);
  }

  getPersonSimple(id: number): Observable<IContactOrPerson> {
    return this.observableGet("api/person/" + id + "/simple");
  }

  async getCompanyEmail(id: number): Promise<string> {
    return await this.get("api/contact/" + id + "/email");
  }

  getPersonCard(id: number): Promise<IPersonCard | null> {
    return this.get<IPersonCard | null>("api/person/" + id + "/card");
  }

  getCompanyCard(id: number): Promise<ICompanyCard | null> {
    return this.get<ICompanyCard | null>("api/contact/" + id + "/card");
  }

  getPersonImage(id: number): Observable<string> {
    return this.observableGet<string>("api/person/" + id + "/image")
      .pipe(
        map(x => x),
        catchError(err => {
          return of(null);
        }));
  }

  async getContactSuggestions(senderName: string, senderEmail: string): Promise<IContactOrPerson[]> {
    return await this.get(`api/person/suggestions?email=${encodeURIComponent(senderEmail)}&name=${encodeURIComponent(senderName)}`);
  }

  // Silent token only: an automatic suggestion must never open a sign-in window.
  getContactPrefill(request: IContactPrefillRequest): Observable<IContactPrefillResponse> {
    return this.withNaaAuthObservable(
      headers => this.httpClient.post<IContactPrefillResponse>(`${this.baseUrl}api/people/contactPrefill`, request, { headers }),
      { operation: "POST api/people/contactPrefill", allowInteractive: false }
    ).pipe(
      catchError(error => {
        // Status and error code only: an MSAL error message can carry account details.
        this.monitoringService.logCustomEvent("FAILED: contact prefill", {
          status: error instanceof HttpErrorResponse ? error.status : null,
          errorCode: this.getAuthErrorCode(error),
        });
        return throwError(() => error);
      })
    );
  }

  // NAA auth carries both the fresh Graph token (NAA-Authorization header, read server-side to
  // fetch the thread) and, when applicable, the X-Shared-Mailbox header - same path as the
  // preupload calls above, so a shared-mailbox archive gets both for free.
  async summarizeThread(itemId: string, isoLangCode: string): Promise<AiThreadSummaryResponse> {
    try {
      return await this.postWithNaaAuth<AiThreadSummaryResponse>(
        "api/Ai/summarizeThread",
        { itemId, isoLangCode }
      );
    } catch (e) {
      throw toAiSummaryError(e);
    }
  }

  async getNamedPreferences(): Promise<NamedPreference[]> {
    // Check undefined, not length — an empty result is still a valid, cacheable answer.
    // No in-flight dedup — fine while callers invoke this sequentially.
    if (!this.namedPreferences) {
      this.namedPreferences = await this.get("api/preference/named");
    }
    return this.namedPreferences;
  }

  async getCurrentTimezone(): Promise<ICurrentTimezone> {
    return await this.get("api/timezone/current");
  }

  getCustomData(appointmentExtension: IAppointmentExtension): Observable<ICustomData> {
    const contactId = appointmentExtension.contactId;
    const personId = appointmentExtension.personId;
    const projectId = appointmentExtension.projectId;
    const saleId = appointmentExtension.saleId;
    const appointmentTypeId = appointmentExtension.appointmentTypeId;

    const queryParam = `contactId=${contactId}&personId=${personId}&projectId=${projectId}&saleId=${saleId}&appointmentTypeId=${appointmentTypeId}`
    return this.observableGet(`api/customData?${encodeURI(queryParam)}`);
  }

  async getTasksMdoList(): Promise<IMdoItem[]> {
    return await this.get("api/mdo/task");
  }

  getCreatePersonDialogState(
    senderName: string,
    senderEmail: string,
    companyId: number,
    contactId: number
  ): Observable<ICreateDialogState> {
    return this.observablePost("api/person/dialogstate", {
      SenderEmailAddress: senderEmail,
      SenderName: senderName,
      ContactId: companyId,
      PersonId: contactId
    }, this.httpHeaders).pipe(map((response: ICreateDialogState) => {
      const defaultMrMs = {
        Id: 999,
        Name: "",
        Rank: 1,
        ChildItems: null,
        MdoType: "Item",
        FileName: null
      } as IMdoItem;

      response.MrMrs.unshift(defaultMrMs);
      return response;
    }))
  }

  async addEmailToPerson(
    companyId: number,
    personId: number,
    email: string,
    isThisPrimaryEmail: boolean
  ): Promise<IContactOrPerson> {
    return await this.post("api/person/email", {
      ContactId: companyId,
      PersonId: personId,
      EmailAddress: email,
      MakePrimaryEmailAddress: isThisPrimaryEmail
    });
  }

  async createCompanyOrContact(companyOrContact: ICreateCompanyOrContact): Promise<IContactOrPerson> {
    return await this.post("api/person", companyOrContact);
  }

  /* activity service replacement */

  async createActivity(
    contactId: number,
    personId: number,
    projectId: number,
    saleId: number,
    taskId: number,
    uniqueId: string,
    description: string,
    doDate: string,
    visibleFor: IVisibleFor
  ): Promise<number> {
    return await this.post("api/appointment", {
      ContactId: contactId,
      PersonId: personId,
      ProjectId: projectId,
      SaleId: saleId,
      TaskId: taskId,
      UniqueId: uniqueId,
      Description: description,
      DoDate: doDate,
      SmtpId: null,
      VisibleFor: visibleFor,
    });
  }

  async createTask(
    contactId: number,
    personId: number,
    projectId: number,
    saleId: number,
    taskId: number,
    uniqueId: string,
    description: string,
    doDate: Date,
    visibleFor: IVisibleFor
  ): Promise<number> {

    // retrieves a default-object. does not save anything in DB yet.
    const request = await this.post<any, AppointmentEntity>("api/task/default", {} // 6 = Todo
    );

    // we're not really a Date, but we're date-formatted.
    // Good enough for the server, so lets just coerce the compiler ;)
    const reqDate = <any>this.getDateString(doDate);

    // patch up!
    request.Task = { TaskListItemId: taskId } as any;
    request.Contact = { ContactId: contactId } as any;
    request.Person = { PersonId: personId } as any;
    request.Project = { ProjectId: projectId } as any;
    request.Sale = { SaleId: saleId } as any;
    request.StartDate = reqDate;
    request.EndDate = reqDate;
    request.ActiveDate = reqDate;
    request.Description = description;
    request.VisibleFor = this.getVisibleFor(visibleFor);
    request.ExtraFields = {
      "CRM5 Mailbox.e-mail.Message-Id": uniqueId
    };
    // this is what is required to make it show in the checklist, not diary!
    request.Type = "inChecklist";

    const response = await this.post<AppointmentEntity, AppointmentEntity>("api/task/appointment", request);

    return response.AppointmentId;
  }

  getAppointmentEntity(id: number): Observable<AppointmentCreator> {
    return this.observableGet<AppointmentCreator>(`api/appointment/appointmententity/${id}`);
  }

  getDateString(date: Date): string {
    const year = date.getFullYear().toString();
    let month = (date.getMonth() + 1).toString();
    let day = date.getDate().toString();

    if (month.length === 1) {
      month = "0" + month;
    }

    if (day.length === 1) {
      day = "0" + day;
    }

    // intentionally no timezone or UTC marker!
    // adding that results in time being readjust based on server/tenant/db timezone!
    return `${year}-${month}-${day}T00:00:00.000`;
  }

  getVisibleFor(visibleFor: IVisibleFor): VisibleFor[] | null {
    if (!visibleFor) {
      return null;
    } else if (visibleFor.Id === -1) {
      return [{
        Visibility: "All",
        VisibleId: 0
      }];
    } else if (!visibleFor.IsGroup) {
      return [{
        Visibility: "Associate",
        VisibleId: visibleFor.Id
      }];
    } else {
      return [{
        Visibility: "Group",
        VisibleId: visibleFor.Id
      }];
    }
  }

  get httpHeaders(): HttpHeaders {
    return new HttpHeaders({ "Accept-Language": this.outlookService.displayLanguage });
  };

  /* archive service replacement */

  async preloadEMLByMessageIdBatch(
    messageId: string,
    preuploadWithAndWithoutAttachments: boolean
  ): Promise<IPreUploadByMessageBatchResponse> {
    const response = await this.postWithNaaAuth<IPreUploadByMessageBatchRawResponse>(
      "api/document/preupload/message/batch",
      { MessageId: messageId, PreuploadWithAndWithoutAttachments: preuploadWithAndWithoutAttachments }
    );
    return this.normalizePreuploadBatchResponse(response);
  }

  async getPreuploadDecisionByMessageId(messageId: string): Promise<IPreUploadByMessageDecisionResponse> {
    const response = await this.postWithNaaAuth<IPreUploadByMessageDecisionRawResponse>(
      "api/document/preupload/message/decision",
      { MessageId: messageId }
    );
    return this.normalizePreuploadDecisionResponse(response);
  }

  async fetchExtensionData(eventId: string): Promise<IAppointmentExtension | null> {
    return this.withNaaAuth(async headers => {
      const url = `${this.baseUrl}api/graph/events/${encodeGraphEventId(eventId)}/extension`;
      try {
        return await firstValueFrom(this.httpClient.get<IAppointmentExtension>(url, { headers }));
      } catch (error) {
        if ((error as HttpErrorResponse).status === 404) return null;
        throw error;
      }
    }, { operation: "GET api/graph/events/extension" });
  }

  async createExtensionData(eventId: string, extension: IAppointmentExtension): Promise<void> {
    return this.withNaaAuth(headers => {
      const url = `${this.baseUrl}api/graph/events/${encodeGraphEventId(eventId)}/extension`;
      return firstValueFrom(this.httpClient.post<void>(url, extension, { headers }));
    }, { operation: "POST api/graph/events/extension" });
  }

  // Kept Observable end-to-end, unlike withNaaAuth's Promise, so switchMap can actually cancel it.
  createExtensionDataObservable(eventId: string, extension: IAppointmentExtension): Observable<void> {
    return this.withNaaAuthObservable(headers => {
      const url = `${this.baseUrl}api/graph/events/${encodeGraphEventId(eventId)}/extension`;
      return this.httpClient.post<void>(url, extension, { headers });
    }, { operation: "POST api/graph/events/extension (observable)" });
  }

  private withNaaAuthObservable<T>(call: (headers: HttpHeaders) => Observable<T>, options: INaaAuthOptions): Observable<T> {
    const allowInteractive = options.allowInteractive ?? true;
    return defer(() => from(this.authService.getAccessToken(false, undefined, allowInteractive))).pipe(
      switchMap(naaToken => call(this.buildNaaHeaders(naaToken))),
      catchError(error => {
        const claimChallenge = this.getClaimChallenge(error);
        if (!claimChallenge) return throwError(() => error);
        this.logClaimsChallengeRetry(options.operation, allowInteractive);
        return defer(() => from(this.authService.getAccessToken(false, claimChallenge, allowInteractive))).pipe(
          switchMap(naaToken => call(this.buildNaaHeaders(naaToken))),
          catchError(retryError => {
            this.logClaimsChallengeRetryFailed(options.operation, retryError);
            return throwError(() => retryError);
          })
        );
      })
    );
  }

  async deleteExtensionData(eventId: string): Promise<void> {
    return this.withNaaAuth(headers => {
      const url = `${this.baseUrl}api/graph/events/${encodeGraphEventId(eventId)}/extension`;
      return firstValueFrom(this.httpClient.delete<void>(url, { headers }));
    }, { operation: "DELETE api/graph/events/extension" });
  }

  async applyPendingCategoryTags(): Promise<void> {
    return this.withNaaAuth(headers => {
      const url = `${this.baseUrl}api/graph/category-tags/apply-pending`;
      // Fired from a resolver, so the route-change interceptor would cancel it mid-flight.
      const context = new HttpContext().set(SURVIVES_ROUTE_CHANGE, true);
      return firstValueFrom(this.httpClient.post<void>(url, null, { headers, context }));
    }, { operation: "POST api/graph/category-tags/apply-pending", allowInteractive: false });
  }

  private async postWithNaaAuth<TResponse>(urlSuffix: string, payload: object): Promise<TResponse> {
    return this.withNaaAuth(headers => this.post<object, TResponse>(urlSuffix, payload, headers), { operation: `POST ${urlSuffix}` });
  }

  private async withNaaAuth<T>(call: (headers: HttpHeaders) => Promise<T>, options: INaaAuthOptions): Promise<T> {
    const allowInteractive = options.allowInteractive ?? true;
    try {
      const naaToken = await this.authService.getAccessToken(false, undefined, allowInteractive);
      return await call(this.buildNaaHeaders(naaToken));
    } catch (error) {
      const claimChallenge = this.getClaimChallenge(error);
      if (!claimChallenge) throw error;
      this.logClaimsChallengeRetry(options.operation, allowInteractive);
      try {
        const naaToken = await this.authService.getAccessToken(false, claimChallenge, allowInteractive);
        return await call(this.buildNaaHeaders(naaToken));
      } catch (retryError) {
        this.logClaimsChallengeRetryFailed(options.operation, retryError);
        throw retryError;
      }
    }
  }

  private logClaimsChallengeRetry(operation: string, allowInteractive: boolean): void {
    this.monitoringService.logCustomEvent("INFO: NAA claims challenge from backend - retrying with claims", {
      operation,
      allowInteractive,
    });
  }

  // errorMessage is deliberately not logged: it can carry account details.
  private logClaimsChallengeRetryFailed(operation: string, error: unknown): void {
    this.monitoringService.logCustomEvent("FAILED: NAA claims retry", {
      operation,
      status: error instanceof HttpErrorResponse ? error.status : null,
      errorCode: this.getAuthErrorCode(error),
    });
  }

  private getAuthErrorCode(error: unknown): string | null {
    const code = (error as { errorCode?: unknown })?.errorCode;
    return typeof code === "string" ? code : null;
  }

  private buildNaaHeaders(naaToken: string): HttpHeaders {
    let headers = new HttpHeaders({ "NAA-Authorization": naaToken });
    const sharedMailbox = this.sharedMailboxService.targetMailbox;
    if (sharedMailbox) {
      headers = headers.set("X-Shared-Mailbox", sharedMailbox);
    }
    return headers;
  }

  private normalizePreuploadBatchResponse(response: IPreUploadByMessageBatchRawResponse): IPreUploadByMessageBatchResponse {
    return {
      WithAttachmentsPreUploadId: response?.WithAttachmentsPreUploadId ?? response?.withAttachmentsPreUploadId ?? null,
      WithoutAttachmentsPreUploadId: response?.WithoutAttachmentsPreUploadId ?? response?.withoutAttachmentsPreUploadId ?? null,
    };
  }

  private normalizePreuploadDecisionResponse(response: IPreUploadByMessageDecisionRawResponse): IPreUploadByMessageDecisionResponse {
    const mode: PreuploadDecisionMode = response?.Mode ?? response?.mode ?? "both";
    return {
      Mode: mode,
    };
  }

  private getClaimChallenge(error: any): string | null {
    const httpError = error as HttpErrorResponse;
    if (!httpError || httpError.status !== 401) {
      return null;
    }

    if (typeof httpError.error === "string" && httpError.error.trim().length > 0) {
      return this.normalizeClaimChallenge(httpError.error);
    }

    if (httpError.error && typeof httpError.error.claimChallenge === "string") {
      return this.normalizeClaimChallenge(httpError.error.claimChallenge);
    }

    // A bare top-level access_token/id_token means a controller forgot
    // [Produces("application/json")], so ASP.NET Core sent unescaped text and JSON.parse gave
    // back this object. Handled here so retry works regardless.
    if (httpError.error && typeof httpError.error === "object"
      && ("access_token" in httpError.error || "id_token" in httpError.error)) {
      return this.normalizeClaimChallenge(JSON.stringify(httpError.error));
    }

    return null;
  }

  private normalizeClaimChallenge(value: string): string | null {
    const challenge = value?.trim();
    if (!challenge) {
      return null;
    }

    if (this.isValidClaimChallengeJson(challenge)) {
      return challenge;
    }

    const decodedDirectChallenge = this.decodeClaims(challenge);
    if (decodedDirectChallenge && this.isValidClaimChallengeJson(decodedDirectChallenge)) {
      return decodedDirectChallenge;
    }

    const claimsValue = this.extractClaimsValue(challenge);
    if (claimsValue) {
      const decodedClaims = this.decodeClaims(claimsValue);
      return decodedClaims && this.isValidClaimChallengeJson(decodedClaims) ? decodedClaims : null;
    }

    return this.looksLikeWwwAuthenticateHeader(challenge) ? null : challenge;
  }

  private extractClaimsValue(challengeHeader: string): string | null {
    const quotedPattern = /(?:^|,)\s*claims\s*=\s*"([^"]+)"/i;
    const quotedMatch = challengeHeader.match(quotedPattern);
    if (quotedMatch?.[1]) {
      return quotedMatch[1];
    }

    const unquotedPattern = /(?:^|,)\s*claims\s*=\s*([^,\s]+)/i;
    const unquotedMatch = challengeHeader.match(unquotedPattern);
    return unquotedMatch?.[1] ?? null;
  }

  private decodeClaims(encodedClaims: string): string | null {
    try {
      const urlDecoded = decodeURIComponent(encodedClaims);
      const base64 = urlDecoded.replace(/-/g, "+").replace(/_/g, "/");
      const paddedBase64 = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
      return atob(paddedBase64);
    } catch {
      return null;
    }
  }

  private isValidClaimChallengeJson(value: string): boolean {
    try {
      const parsed = JSON.parse(value) as unknown;
      return !!parsed && typeof parsed === "object";
    } catch {
      return false;
    }
  }

  private looksLikeWwwAuthenticateHeader(value: string): boolean {
    return /(^|\s)Bearer\s+/i.test(value)
      || /\b(?:authorization_uri|error|resource_id|realm)\s*=/i.test(value);
  }

    /**
   * Preload the attachment that needs to be archived
   *
   * @param sEMLData - base64 encoded attachment data, format eml
   */
    async preloadAttachment(sEMLData: string): Promise<string> {
      return await this.post("api/document/preupload/attachment", {
        EncodedContents: sEMLData
      });
    }

  /**
   * Delete the preloaded document
   *
   * @param preloadID - Preloaded document id
   */
  async deletePreloadedEML(preloadID: string): Promise<boolean> {
    if (!preloadID) {
      // clearly an error, but lets not fail over it.
      return false;
    } else {
      return await this.delete(`api/document/preupload/${encodeURI(preloadID)}`);
    }
  }

  beaconDeletePreuploads(ids: string[]): void {
    if (ids.length === 0) {
      return;
    }

    this.beaconPost("api/document/preupload/batch-delete", ids);
  }

  /**
   * Archive a mail
   *
   * @param archive - Archive post body informations
   */
  async postDocument(archive: IArchive): Promise<number> {
    const result = await this.post<IArchive, number>("api/document", archive);
    this.invalidateDocumentDialogstateCache(archive?.UniqueId);
    return result;
  }

  /* compose service replacement */

  /**
   * Find the significant contact/company
   *
   * @param emails mail id's in an array
   */
  async findSignificantContact(emails: string[]): Promise<IContactOrPerson> {
    return await this.post("api/person/findSignificantContactByEmails", { emails });
  }

  /* find service replacement */

  /**
   * Checks whether the sender exists in the CRM
   *
   * @param email Sender's email address
   * @returns Observable of IFindSenderByMail[]
   */
  async findMailSender(
    email: string,
    extraEmail: string[]
  ): Promise<IFindSenderByMail[]> {
    return await this.post("api/person/findByEmail", {
      Text: email,
      ExtraTexts: extraEmail,
    });
  }

  findByEmail(
    email: string,
    extraEmail: string[]
  ): Observable<IFindSenderByMail[]> {
    return this.observablePost("api/person/findByEmail", {
      Text: email,
      ExtraTexts: extraEmail,
    });
  }

  /**
   * Finds all matching Contact/Company
   *
   * @param name Search key - Contact/Company name
   */
  async findContacts(name: string): Promise<IContactOrPerson[]> {
    if (!name) {
      return [];
    } else {
      return await this.get(`api/contact?name=${encodeURIComponent(name)}`);
    }
  }

  /**
   * Finds all matching Contact/Person
   *
   * @param name Contact/Person name
   * @param contactId Company/Contact id
   */
  async findPersons(name: string, contactId: number): Promise<IContactOrPerson[]> {
    if (contactId === 0 && !name) {
      return [];
    } else {
      return await this.get(`api/person?name=${encodeURIComponent(name)}&contactId=${contactId}`);
    }
  }

  /**
   * Returns all persons belonging to a specific company/contact.
   * Used when expanding a company row in the company-contact combobox.
   *
   * @param contactId Company/Contact id
   */
  async getPersonsByContact(contactId: number): Promise<IContactOrPerson[]> {
    return await this.get(`api/person/bycontact/${contactId}`);
  }

  /**
   * Get history data for both contact & person
   *
   * @param entity contacts or persons
   */
  async getContactsOrPersonsHistory(entity: Entity): Promise<IContactOrPerson[]> {
    switch (entity) {
      case Entity.Company:
        return await this.get(`api/contact/history`);
      case Entity.Contact:
        return await this.get(`api/person/history`);
    }
  }

  async findSales(name: string): Promise<ISale[]> {
    if (!name) {
      return [];
    } else {
      return await this.get(`api/sale?name=${encodeURIComponent(name)}`);
    }
  }

  async getSaleHistory(): Promise<ISale[]> {
    return await this.get("api/sale/history");
  }

  async getAssociatedSales(entity: IContactOrPerson): Promise<ISale[]> {
    if (!entity || (entity.PersonId === 0 && entity.ContactId === 0)) {
      return [];
    } else {
      return await this.post("api/sale/associated", entity);
    }
  }

  async findProjects(name: string): Promise<IProject[]> {
    if (!name) {
      return [];
    } else {
      return await this.get(`api/project?name=${encodeURIComponent(name)}`);
    };
  }

  async getProjectHistory(): Promise<IProject[]> {
    return await this.get("api/project/history");
  }

  async getAssociatedProjects(entity: IContactOrPerson): Promise<IProject[]> {
    if (!entity || (entity.PersonId === 0 && entity.ContactId === 0)) {
      return [];
    } else {
      return await this.post("api/project/associated", entity);
    }
  }

  /* dialogstate service replacement */

  async getPreferencesDialogstate(): Promise<IPreferencesDialogstate> {
    if (!this.preferences) {
      this.preferences = await this.fetchPreferencesDialogstate();
    }
    return this.preferences;
  }

  fetchPreferencesDialogstate(): Promise<IPreferencesDialogstate> {
    return this.post("api/preference/dialogstate", {}, this.httpHeaders);
  }

  async getPreferences(): Promise<IPreference[]> {
    return await this.get("api/preference");
  }

  async savePreferences(prefs: IPreference[]): Promise<IPreference[]> {
    return await this.post("api/preference", prefs);
  }


  /**
 * This method returns whether the mail is already archived or not
 *
 * @param email lookup email address
 * @param uniqueId lookup email unique id
 */
  async getDocumentDialogstate(
    email: string,
    uniqueId: string,
    isOutbound: boolean
  ): Promise<IDocumentDialogstate> {
    const cacheKey = this.getDocumentDialogstateCacheKey(email, uniqueId, isOutbound);

    if (this.documentDialogstateCache.has(cacheKey)) {
      return this.documentDialogstateCache.get(cacheKey)!;
    }

    const inFlightRequest = this.documentDialogstateInFlight.get(cacheKey);
    if (inFlightRequest) {
      return await inFlightRequest;
    }

    const request = this.post<any, IDocumentDialogstate>("api/document/dialogstate", {
      EmailAddress: email,
      IsOutbound: isOutbound,
      UniqueId: uniqueId,
    }, this.httpHeaders).then((result) => {
      this.documentDialogstateCache.set(cacheKey, result);
      return result;
    }).finally(() => {
      this.documentDialogstateInFlight.delete(cacheKey);
    });

    this.documentDialogstateInFlight.set(cacheKey, request);
    return await request;
  }

  /**
   * This method returns whether the mail is already archived or not
   *
   * @param email lookup email address
   * @param uniqueId lookup email unique id
   */
  getDocumentDialogstateObservable(
    email: string,
    uniqueId: string,
    isOutbound: boolean
  ): Observable<IDocumentDialogstate> {
    return from(this.getDocumentDialogstate(email, uniqueId, isOutbound));
  }

  private getDocumentDialogstateCacheKey(email: string, uniqueId: string, isOutbound: boolean): string {
    return JSON.stringify({
      email: email ?? "",
      uniqueId: uniqueId ?? "",
      isOutbound
    });
  }

  private invalidateDocumentDialogstateCache(uniqueId: string): void {
    if (!uniqueId) {
      return;
    }

    for (const key of Array.from(this.documentDialogstateCache.keys())) {
      if (this.getUniqueIdFromDocumentDialogstateCacheKey(key) === uniqueId) {
        this.documentDialogstateCache.delete(key);
      }
    }

    for (const key of Array.from(this.documentDialogstateInFlight.keys())) {
      if (this.getUniqueIdFromDocumentDialogstateCacheKey(key) === uniqueId) {
        this.documentDialogstateInFlight.delete(key);
      }
    }
  }

  private getUniqueIdFromDocumentDialogstateCacheKey(cacheKey: string): string | null {
    try {
      const parsed = JSON.parse(cacheKey) as { uniqueId?: string };
      return parsed?.uniqueId ?? null;
    } catch {
      return null;
    }
  }

  /**
   * requestCapabilitiesPrivileged()
   * Request to capabilities/privileged
   */
  async getCapabilitiesPrivileged(): Promise<ICapabilitiesPrivileged[]> {
    // No in-flight dedup — fine while callers invoke this sequentially.
    if (!this.capabilities) {
      this.capabilities = await this.getCapabilities();
    }
    return this.capabilities;
  }

  private async getCapabilities(): Promise<ICapabilitiesPrivileged[]> {
    return this.get("api/preference/capabilities");
  }

  /**
   * public getTasksDialogstate() - get mail activity informations
   * This is called everytime a new mail is seleted
   *
   * @param SenderEmailAddress - mail sender
   * @param SenderName - mail sender name
   * @param UniqueId - mail internet message id
   */
  getTasksDialogstate(
    SenderEmailAddress: string,
    SenderName: string,
    UniqueId: string,
    UniqueIds: string[]
  ): Observable<ITasksDialogstate> {
    return this.observablePost("api/task/dialogstate", {
      SenderEmailAddress,
      SenderName,
      UniqueId,
      UniqueIds,
    }, this.httpHeaders);
  }

  /**
 * public getActivityDialogstate() - get mail activity informations
 * This is called everytime a new mail is seleted
 *
 * @param SenderEmailAddress - mail sender
 * @param SenderName - mail sender name
 * @param UniqueId - mail internet message id
 */
  getActivityDialogstate(
    SenderEmailAddress: string,
    SenderName: string,
    UniqueId: string,
    UniqueIds: string[]
  ): Observable<ITasksDialogstate> {
    return this.observablePost("api/appointment/dialogstate", {
      SenderEmailAddress,
      SenderName,
      UniqueId,
      UniqueIds,
    }, this.httpHeaders);
  }

  /* request service replacement */

  getMailboxes(): Observable<ITicketMailBoxes[]> {
    return this.observableGet("api/ticket/mailboxes");
  }

  async postTicket(filterId: number, messageId: string, personId: number): Promise<ITicketMessageMailBoxes> {
    return await this.postWithNaaAuth<ITicketMessageMailBoxes>("api/ticket", {
      filterId,
      messageId,
      personId,
    });
  }

  async findTicket(searchTerm: string): Promise<ITicketMessageMailBoxes[]> {
    if (!searchTerm) {
      return [];
    }

    if (!this.isNumeric(searchTerm) && searchTerm.length < 2) {
      return [];
    }

    return await this.get(`api/ticket?name=${encodeURIComponent(searchTerm)}`);
  }

  isNumeric(value: string): boolean {
    try {
      return !isNaN(Number.parseInt(value, 0));
    } catch {
      return false;
    }
  }

  async addMessageToTicket(ticketId: number, messageId: string): Promise<ITicketMessageMailBoxes> {
    return await this.postWithNaaAuth<ITicketMessageMailBoxes>(`api/ticket/${ticketId}/message`, { messageId });
  }

  /* attachment service replacement */

  getAttachmentDialogState(
    email: string,
    fileExtension: string,
    isOutbound: boolean,
    uniqueId: string,
    uniqueIds: string[]
  ): Observable<IDocumentDialogstate> {
    return this.observablePost("api/document/dialogstate", {
      EmailAddress: email,
      FileExtension: fileExtension,
      IsOutbound: isOutbound,
      UniqueId: uniqueId,
      UniqueIds: uniqueIds,
    });
  }

  async saveAttachment(
    contactId: number,
    personId: number,
    saleId: number,
    projectId: number,
    documentId: number,
    preuploadId: string,
    attachmentId: string,
    title: string,
    fileName: string,
    visibleFor: IVisibleFor | null,
    completed: boolean,
    date: string,
    description: string,
    encodedContent: string | null
  ): Promise<number> {
    return await this.post("api/document", {
      Completed: completed,
      ContactId: contactId,
      Date: date,
      Description: description,
      DocumentTemplateId: documentId,
      EncodedContents: encodedContent,
      FileName: fileName,
      PersonId: personId,
      PreuploadedDocumentId: preuploadId,
      ProjectId: projectId,
      SaleId: saleId,
      SmtpId: null,
      Title: title,
      UniqueId: attachmentId,
      VisibleFor: visibleFor,
    });
  }
}

export interface NamedPreference {
  Level: string;
  RawValue: string;
  Specification: NamedPreferenceSpecification;
  DisplayValue: string;
  DisplayTooltip: string;
  DisplayType: string;
  TabOrder: string;
  TargetId: number;
  PrefDescId: number;
  TableName: string;
  UserPreferenceId: number;
  TableRight: string;
}

export interface NamedPreferenceSpecification {
  Section: string;
  Key: string;
  TableRight: string;
}

export interface ICurrentTimezone {
  TZLocationID: number;
  Name: string;
}

export class AppointmentCreator {
  CreatedBy: AssignedBy;

  CreatedDate: string;
}

class AssignedBy {
  AssociateId: number;

  Name: string;

  PersonId: number;

  Rank: number;

  Tooltip: string;

  Type: string;

  GroupIdx: number;

  FullName: string;

  FormalName: string;

  Deleted: boolean;

  EjUserId: number;

  UserName: string;

  TableRight: TableRight;

  FieldProperties: Map<string, Property>;

}

class TableRight {

  Mask: string;

  Reason: string;

}

class Property {

  FieldRight: TableRight;

  FieldType: string;

  FieldLength: number;

}

// IIS/HTTP.sys normalises %2F → '/' before routing, breaking path parameters that contain
// slashes. Double-encode slashes so they survive IIS path normalisation (%252F → %2F after
// one decode pass, then → '/' when ASP.NET Core binds the route parameter).
export function encodeGraphEventId(eventId: string): string {
  return encodeURIComponent(eventId).replace(/%2F/gi, "%252F");
}
